import type { GameType } from '@big2/shared';
import { CardUI } from '../CardUI.js';
import { useI18n } from '../../i18n.js';
import './landingGamesShowcase.css';

export interface LandingGamesShowcaseProps {
  onScrollToLauncher: (targetGame?: GameType) => void;
  onOpenRules?: (gameType: GameType) => void;
}

export function LandingGamesShowcase({ onScrollToLauncher, onOpenRules }: LandingGamesShowcaseProps) {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';

  return (
    <section className="landing-section" id="games-showcase" aria-label={t.landingGamesTitle}>
      <div className="landing-section-header">
        <h2>{t.landingGamesTitle}</h2>
        <p>{t.landingGamesSubtitle}</p>
      </div>

      {/* Tier 1: Flagship Big Two Spotlight */}
      <article className="big2-flagship-card">
        <div className="big2-flagship-info">
          <div className="game-card-meta">
            <span className="game-pill gold">♠ {isMn ? 'Үндсэн тоглоом' : 'Flagship Game'}</span>
            <span className="game-pill">2–4 {isMn ? 'Тоглогч' : 'Players'}</span>
            <span className="game-pill">52 {isMn ? 'Мод (13/хүн)' : 'Cards (13 each)'}</span>
            <span className="game-pill">{isMn ? 'Покер хослол' : 'Poker Combos'}</span>
          </div>

          <h3 className="game-card-title">♠ {isMn ? 'Арван гурав · Хоёр' : 'Big Two (Arvan Gurav)'}</h3>
          <p className="game-card-desc">{t.landingBig2Desc}</p>

          <ul className="game-card-features">
            <li>
              <span className="feature-pip" aria-hidden="true">♠</span>
              <div>
                <strong>{isMn ? '2♠ Ноёрхогч' : '2♠ Supreme'}</strong> · {isMn ? '3♦ тоглоомыг нээнэ' : '3♦ opens the lead'}
              </div>
            </li>
            <li>
              <span className="feature-pip is-red" aria-hidden="true">♦</span>
              <div>
                <strong>{isMn ? 'Покер хослол' : 'Poker Climbs'}</strong> · {isMn ? 'Стрейт, Флаш, Фул хаус, Каре' : 'Straight, Flush, Full House, Quads'}
              </div>
            </li>
            <li>
              <span className="feature-pip" aria-hidden="true">♣</span>
              <div>
                <strong>{isMn ? 'Хурдан ялалт' : 'Fast Shedding'}</strong> · {isMn ? '13 модоо түрүүлж дуусгана' : 'First to 0 cards wins'}
              </div>
            </li>
            <li>
              <span className="feature-pip is-red" aria-hidden="true">♥</span>
              <div>
                <strong>{isMn ? 'Торгууль' : 'Penalties'}</strong> · {isMn ? '10+ үлдвэл 2×, 13 үлдвэл 3×' : '10+ cards = 2×, 13 cards = 3×'}
              </div>
            </li>
          </ul>

          <div className="game-card-actions">
            <button
              type="button"
              className="btn-play"
              onClick={() => onScrollToLauncher('big2')}
            >
              {t.landingPlayBig2}
            </button>
            {onOpenRules && (
              <button
                type="button"
                className="btn-rules"
                onClick={() => onOpenRules('big2')}
              >
                {t.landingBig2RulesBtn}
              </button>
            )}
          </div>
        </div>

        <div className="big2-flagship-board" aria-hidden="true">
          <div className="big2-board-header">
            <span className="big2-board-title">
              {isMn ? 'Хүчний Эрэмбэ ба 5 Модны Хослол' : 'Card Hierarchy & 5-Card Ladder'}
            </span>
            <span className="big2-suit-order" title={isMn ? 'Өнгөний эрэмбэ' : 'Suit order'}>
              ♦ &lt; ♣ &lt; ♥ &lt; ♠
            </span>
          </div>

          <div className="game-card-visual big2-visual-showcase">
            <div className="card-sample-row">
              <CardUI cardCode="2S" style={{ transform: 'scale(0.58)', margin: '-12px -5px' }} />
              <CardUI cardCode="2H" style={{ transform: 'scale(0.58)', margin: '-12px -5px' }} />
              <CardUI cardCode="KS" style={{ transform: 'scale(0.58)', margin: '-12px -5px' }} />
              <CardUI cardCode="KH" style={{ transform: 'scale(0.58)', margin: '-12px -5px' }} />
              <CardUI cardCode="KD" style={{ transform: 'scale(0.58)', margin: '-12px -5px' }} />
            </div>
            <span className="card-visual-caption">
              {isMn ? 'Жишээ: Ноёны Фулл Хаус (K-K-K-2-2) болон 2♠ Ноёрхогч' : 'Example: Kings Full of Twos (K-K-K-2-2) & 2♠ Boss'}
            </span>
          </div>

          <ol className="big2-combo-ladder">
            <li>
              <span className="ladder-rank">1</span>
              <span className="ladder-name">{isMn ? 'Стрейт' : 'Straight'}</span>
              <span className="ladder-desc">{isMn ? 'Дараалсан 5' : '5 sequential ranks'}</span>
            </li>
            <li>
              <span className="ladder-rank">2</span>
              <span className="ladder-name">{isMn ? 'Флаш' : 'Flush'}</span>
              <span className="ladder-desc">{isMn ? 'Нэг өнгийн 5' : '5 same suit'}</span>
            </li>
            <li>
              <span className="ladder-rank">3</span>
              <span className="ladder-name">{isMn ? 'Фулл хаус' : 'Full House'}</span>
              <span className="ladder-desc">{isMn ? 'Гурвал + Хос' : '3 of a Kind + Pair'}</span>
            </li>
            <li>
              <span className="ladder-rank">4</span>
              <span className="ladder-name">{isMn ? 'Каре' : 'Four of a Kind'}</span>
              <span className="ladder-desc">{isMn ? 'Ижил 4 мод' : '4 equal ranks'}</span>
            </li>
            <li className="ladder-top">
              <span className="ladder-rank">5</span>
              <span className="ladder-name">{isMn ? 'Стрейт флаш' : 'Straight Flush'}</span>
              <span className="ladder-desc">{isMn ? 'Ижил өнгийн цуваа' : 'Same-suit sequence'}</span>
            </li>
          </ol>
        </div>
      </article>

      {/* Tier 2: Extra / Bonus Games */}
      <div className="extra-games-block" aria-label={isMn ? 'Нэмэлт тоглоомууд' : 'Extra Games'}>
        <div className="extra-games-header">
          <div className="extra-games-title-group">
            <h3 className="extra-games-heading">
              {isMn ? 'Бусад нэмэлт тоглоомууд' : 'Extra Club Games'}
            </h3>
          </div>
          <p className="extra-games-sub">
            {isMn
              ? 'Завсарлагаанаар өрөөгөө солилгүй тоглох боломжтой нэмэлт тоглоомууд:'
              : 'Classic club games you can launch in the same room:'}
          </p>
        </div>

        <div className="extra-games-grid">
          {/* Muushig Extra Card */}
          <article className="extra-game-card">
            <div className="extra-game-top">
              <div className="game-card-meta">
                <span className="game-pill">2–5 {isMn ? 'Тоглогч' : 'Players'}</span>
                <span className="game-pill">32 {isMn ? 'Мод (7–А)' : 'Cards (7–A)'}</span>
                <span className="game-pill">{isMn ? '15 → 0 Оноо' : '15 to 0 Pts'}</span>
              </div>
              <h3 className="extra-game-title">🎴 {isMn ? 'Монгол Муушиг (Muushig)' : 'Mongolian Muushig'}</h3>
              <p className="extra-game-desc">{t.landingMuushigDesc}</p>
              <ul className="extra-game-highlights">
                <li>
                  <strong>{isMn ? 'Мод солих:' : 'Stock Swap:'}</strong>{' '}
                  {isMn ? '3 хүртэл мод солих эсвэл өнжинө' : 'Swap up to 3 cards or fold'}
                </li>
                <li>
                  <strong>{isMn ? 'Гэр авах:' : 'Tricks:'}</strong>{' '}
                  {isMn ? 'Гэр бүр -1 оноо, муушиг +5' : 'Each trick removes 1 pt (+5 penalty)'}
                </li>
              </ul>
            </div>

            <div className="extra-game-actions">
              <button
                type="button"
                className="btn-extra-play"
                onClick={() => onScrollToLauncher('muushig')}
              >
                {t.landingPlayMuushig}
              </button>
              {onOpenRules && (
                <button
                  type="button"
                  className="btn-extra-rules"
                  onClick={() => onOpenRules('muushig')}
                >
                  {t.landingMuushigRulesBtn}
                </button>
              )}
            </div>
          </article>

          {/* Daaluu Extra Card */}
          <article className="extra-game-card">
            <div className="extra-game-top">
              <div className="game-card-meta">
                <span className="game-pill">5 {isMn ? 'Тоглогч' : 'Players'}</span>
                <span className="game-pill">50 {isMn ? 'Мод' : 'Tiles'}</span>
                <span className="game-pill">{isMn ? 'Уламжлалт Даалуу' : 'Tile Trick Game'}</span>
              </div>
              <h3 className="extra-game-title">🂍 {isMn ? 'Монгол Даалуу (Daaluu)' : 'Mongolian Daaluu'}</h3>
              <p className="extra-game-desc">
                {isMn
                  ? '5 тоглогч ширээн дээр даалуугаа хамтдаа хольж, жанлий дуудаж тоглоно.'
                  : 'Authentic 5-player Mongolian tile game with live tabletop mixing and Janlii calls.'}
              </p>
              <ul className="extra-game-highlights">
                <li>
                  <strong>{isMn ? 'Мод холих:' : 'Tile Mixing:'}</strong>{' '}
                  {isMn ? 'Ширээн дээр бодитоор даалуугаа эргүүлнэ' : 'Mix tiles together in real time'}
                </li>
                <li>
                  <strong>{isMn ? 'Жанлий:' : 'Janlii:'}</strong>{' '}
                  {isMn ? 'Их модоор гэр авч, цай тооцно' : 'Declare pairs, capture houses, settle tea'}
                </li>
              </ul>
            </div>

            <div className="extra-game-actions">
              <button
                type="button"
                className="btn-extra-play"
                onClick={() => onScrollToLauncher('daaluu')}
              >
                {isMn ? 'Даалуу тоглох' : 'Play Daaluu'}
              </button>
              {onOpenRules && (
                <button
                  type="button"
                  className="btn-extra-rules"
                  onClick={() => onOpenRules('daaluu')}
                >
                  {isMn ? 'Даалуу дүрэм' : 'Daaluu Rules'}
                </button>
              )}
            </div>
          </article>

          {/* Coming Soon Card */}
          <article className="extra-game-card coming-soon-card">
            <div className="extra-game-top">
              <div className="game-card-meta">
                <span className="game-pill gold">✨ {isMn ? 'Тун удахгүй' : 'Coming Soon'}</span>
                <span className="game-pill">{isMn ? 'Шинэ тоглоом' : 'New Additions'}</span>
              </div>
              <h3 className="extra-game-title">🎲 {isMn ? 'Цаашид нэмэгдэх тоглоомууд' : 'More Games Coming Soon'}</h3>
              <p className="extra-game-desc">
                {isMn
                  ? 'Уламжлалт хөзөр, хөлөгт болон бусад сонирхолтой тоглоомуудыг тун удахгүй хүргэхээр бэлтгэж байна.'
                  : 'More traditional parlor favorites and casual club games are in the works. Stay tuned for new additions!'}
              </p>
              <ul className="extra-game-highlights">
                <li>
                  <strong>{isMn ? 'Бэлтгэлд:' : 'In Pipeline:'}</strong>{' '}
                  {isMn ? '21 (Очко), Буур, шинэ тоглоомын горимууд' : '21 (Ochko), Buur & more table modes'}
                </li>
                <li>
                  <strong>{isMn ? 'Санал хүсэлт:' : 'Suggestions:'}</strong>{' '}
                  {isMn ? 'Тоглогчдын санал хүсэлтээр тасралтгүй баяжина' : 'Evolving with player requests and community suggestions'}
                </li>
              </ul>
            </div>

            <div className="extra-game-actions">
              <div className="coming-soon-status">
                <span className="coming-soon-dot" aria-hidden="true" />
                <span>{isMn ? 'Хөгжүүлэлтийн шатанд явагдаж байна' : 'Currently in development'}</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
